import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiExternalLink, FiGlobe, FiMessageSquare, FiPhone } from 'react-icons/fi';

import Logo from '../components/common/Logo';

/**
 * Crisis support.
 *
 * Deliberately the plainest page in the app: no glass, no animation, no
 * marketing. Someone arriving here is not browsing. The numbers come first,
 * above everything else, and nothing on the page asks them to sign up or
 * search for anything.
 *
 * These are real, published helplines. If you deploy this outside India,
 * replace them — a stale crisis number is worse than none.
 */
const HELPLINES = [
  {
    name: 'Tele-MANAS',
    detail: 'Government of India national mental health helpline',
    number: '14416',
    href: 'tel:14416',
    hours: '24 hours, every day · free',
  },
  {
    name: 'AASRA',
    detail: 'Suicide prevention and emotional support',
    number: '+91 9820466726',
    href: 'tel:+919820466726',
    hours: '24 hours, every day',
  },
  {
    name: 'iCALL',
    detail: 'Psychosocial helpline, TISS',
    number: '+91 9152987821',
    href: 'tel:+919152987821',
    hours: 'Mon–Sat, 10am–8pm',
  },
  {
    name: 'Vandrevala Foundation',
    detail: 'Free counselling and crisis intervention',
    number: '+91 9999666555',
    href: 'tel:+919999666555',
    hours: '24 hours, every day',
  },
];

const EMERGENCY = [
  { label: 'Emergency services (India)', number: '112', href: 'tel:112' },
  { label: 'Ambulance', number: '108', href: 'tel:108' },
];

export default function CrisisPage() {
  return (
    <div className="mm-crisis-page">
      <header className="mm-crisis-page__bar">
        <Container className="d-flex align-items-center justify-content-between gap-3">
          <Link to="/" aria-label="MindlyFriend home">
            <Logo markSize={30} />
          </Link>
          <Link to="/" className="mm-auth__back">
            <FiArrowLeft size={15} aria-hidden="true" />
            Back to site
          </Link>
        </Container>
      </header>

      <Container className="mm-crisis-page__body">
        <div className="mm-crisis-page__lede">
          <h1>If you&rsquo;re in crisis, help is available right now.</h1>
          <p>
            You don&rsquo;t need an account, and you don&rsquo;t need to wait for
            an appointment. The lines below are free and answered by trained
            people.
          </p>
        </div>

        {/* Emergency sits above everything else — if someone is in immediate
            danger, no other content on this page matters. */}
        <div className="mm-crisis-page__emergency" role="alert">
          <strong>If you or someone else is in immediate danger, call emergency services.</strong>
          <div className="mm-crisis-page__emergency-numbers">
            {EMERGENCY.map((item) => (
              <a key={item.number} href={item.href} className="mm-crisis-page__emergency-btn">
                <FiPhone size={16} aria-hidden="true" />
                {item.label}
                <strong>{item.number}</strong>
              </a>
            ))}
          </div>
        </div>

        <h2 className="mm-crisis-page__heading">Helplines</h2>
        <Row className="g-3">
          {HELPLINES.map((line) => (
            <Col key={line.name} md={6}>
              <a href={line.href} className="mm-helpline">
                <span className="mm-helpline__icon" aria-hidden="true">
                  <FiPhone size={18} />
                </span>
                <span className="mm-helpline__body">
                  <span className="mm-helpline__name">{line.name}</span>
                  <span className="mm-helpline__detail">{line.detail}</span>
                  <span className="mm-helpline__number">{line.number}</span>
                  <span className="mm-helpline__hours">{line.hours}</span>
                </span>
              </a>
            </Col>
          ))}
        </Row>

        <h2 className="mm-crisis-page__heading">If you&rsquo;d rather not call</h2>
        <Row className="g-3">
          <Col md={6}>
            <div className="mm-crisis-page__card">
              <FiMessageSquare size={18} aria-hidden="true" />
              <div>
                <strong>Text or chat</strong>
                <p>
                  Several services above take WhatsApp and email. Tele-MANAS also
                  offers callbacks if you&rsquo;d rather they ring you.
                </p>
              </div>
            </div>
          </Col>
          <Col md={6}>
            <div className="mm-crisis-page__card">
              <FiGlobe size={18} aria-hidden="true" />
              <div>
                <strong>Outside India</strong>
                <p>
                  <a
                    href="https://findahelpline.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    findahelpline.com
                    <FiExternalLink size={12} className="ms-1" aria-hidden="true" />
                  </a>{' '}
                  lists verified crisis lines in over 130 countries.
                </p>
              </div>
            </div>
          </Col>
        </Row>

        <div className="mm-crisis-page__footnote">
          <p>
            <strong>MindlyFriend is a directory, not an emergency service.</strong>{' '}
            We can&rsquo;t provide urgent care, and nothing you type here is
            monitored. Please use the numbers above.
          </p>
          <p className="mb-0">
            When you&rsquo;re ready to arrange ongoing support,{' '}
            <Link to="/">find a therapist</Link> whenever it suits you.
          </p>
        </div>
      </Container>
    </div>
  );
}
